import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

// Bilingual translation system for CHEKSTE
// COMPLETE TRANSLATION COVERAGE - All UI text centralized here

export const translations = {
  en: {
    // ========== LANDING PAGE ==========
    hero_title: 'AI Copilot for Real Estate Investors',
    hero_subtitle: 'Analyze Real Estate Deals in Seconds',
    hero_description: 'Paste a Zillow, Redfin, or Realtor link or enter a property address and instantly see ARV, rehab estimate, flip profit, rental analysis, and deal score.',
    cta_analyze: 'Analyze a Deal',
    cta_pricing: 'View Pricing',
    cta_login: 'Sign In',
    cta_get_started: 'Get Started',
    
    // ========== NAVIGATION ==========
    nav_dashboard: 'Dashboard',
    nav_analyze: 'Analyze Deal',
    nav_saved: 'Saved Deals',
    nav_compare: 'Compare',
    nav_inbox: 'Deal Inbox',
    nav_account: 'Account',
    nav_billing: 'Billing',
    nav_logout: 'Logout',
    nav_criteria: 'My Criteria',
    nav_admin: 'Admin Analytics',
    
    // ========== ANALYZER INPUT ==========
    input_placeholder: 'Paste property URL or enter address',
    input_analyze_btn: 'Analyze',
    input_url_label: 'Property URL',
    input_address_label: 'Property Address',
    sample_deals_title: 'See a Sample Deal Analysis',
    sample_deals_subtitle: 'Explore how CHEKSTE analyzes real investment properties. Click one of the examples below to see a full deal analysis.',
    sample_fix_flip: 'Sample Fix & Flip Deal',
    sample_rental: 'Sample Rental Investment',
    
    // ========== RESULTS PAGE - CORE NUMBERS ==========
    result_purchase_price: 'Purchase Price',
    result_asking_price: 'Asking Price',
    result_current_value: 'Current Value / Price',
    result_arv: 'Estimated Value',
    result_arv_tooltip: 'Estimated resale value based on comparable properties in your area',
    result_confidence: 'Confidence',
    result_rehab: 'Rehab Cost',
    result_profit: 'Net Profit',
    result_margin: 'Profit Margin',
    result_mao: 'Target Offer Price',
    result_mao_short: 'Target Offer',
    result_mao_tooltip: 'Maximum price you should pay to meet your investment criteria and maintain a profitable margin',
    result_equity_spread: 'Equity Spread',
    result_equity_spread_tooltip: 'Difference between the estimated value and the purchase price.',
    result_potential_equity: 'Potential Equity',
    result_potential_equity_tooltip: 'Difference between estimated value and current price/value',
    result_deal_score: 'Deal Score',
    result_cap_rate: 'Cap Rate',
    result_cashflow: 'Monthly Cashflow',
    result_your_input: 'Your input',
    result_of_est_value: 'of Est. Value',
    result_of_estimated_value: 'of estimated value',
    
    // ========== DEAL CLASSIFICATION ==========
    deal_decision: 'Deal Decision',
    deal_buy: 'BUY',
    deal_negotiate: 'NEGOTIATE',
    deal_pass: 'PASS',
    deal_proceed: 'PROCEED',
    deal_avoid: 'AVOID',
    deal_lender_confidence: 'Lender Confidence',
    deal_confidence_high: 'High',
    deal_confidence_moderate: 'Moderate',
    deal_confidence_low: 'Low',
    deal_why_this_deal: 'Why This Deal',
    deal_how_to_improve: 'How to Improve This Deal',
    deal_improvement_note: 'Conservative recommendations to protect your downside:',
    deal_path_to_deal: 'Path to Deal',
    deal_path_note: 'What gets this deal into your buy range?',
    deal_trust_signal: 'Based on real market data and comparable sales',
    deal_financial_impact: 'Financial Impact',
    deal_below_target: 'This deal is ~${gap} below your target profit',
    deal_above_target: 'Expected profit: ${profit} (+${surplus} above target)',
    deal_highest_impact: 'Highest Impact',
    deal_increases_profit: 'Increases profit by',
    deal_target_price: 'Target price',
    deal_target_rehab: 'Target rehab',
    deal_lower_purchase: 'Lower Purchase Price',
    deal_reduce_rehab: 'Reduce Rehab Scope',
    deal_buy_cash: 'Buy with Cash',
    deal_cash_mode_hint: 'Use Cash Mode toggle above',
    deal_negotiate_price: 'Negotiate a lower purchase price to improve your profit margin',
    deal_eliminate_financing: 'Eliminating financing costs improves margins significantly',
    
    // ========== DEAL MODE TOGGLE ==========
    deal_mode: 'Deal Mode',
    deal_mode_cash: 'Cash',
    deal_mode_financed: 'Financed',
    
    // ========== PRICE TARGET COMPARISON ==========
    price_within_target: 'Price Within Your Target',
    price_exceeds_target: 'Price Exceeds Your Target',
    price_under_target: 'Under Target',
    price_negotiation_gap: 'Negotiation gap',
    price_target_rule: 'Target Rule',
    price_note_profitable: 'Note: This deal shows profit but exceeds your purchase discipline threshold. Consider negotiating.',
    price_based_on_rule: 'Based on your {rule}% rule, the target offer is ${target}. Purchase price (${price}) is {status} this threshold.',
    price_status_within: 'within',
    price_status_above: 'above',
    
    // ========== WHY BULLETS ==========
    why_profit_exceeds: 'Net profit of ${profit} exceeds your ${target} target',
    why_profit_short: 'Net profit of ${profit} falls ~${gap} short of target',
    why_profit_negative: 'Negative profit of ${profit} – requires significant negotiation',
    why_investment_healthy: 'Total investment is {pct}% of estimated value (healthy margin)',
    why_investment_tight: 'Total investment is {pct}% of value – margins are tight',
    why_investment_high: 'Total investment is {pct}% of value – too high for safe flip',
    // NEW: Keys that reference user's active rule for consistency
    why_investment_healthy_rule: 'Total investment is {pct}% of estimated value (within your {rule}% threshold)',
    why_investment_tight_rule: 'Total investment is {pct}% of value – above your {rule}% threshold',
    why_investment_high_rule: 'Total investment is {pct}% of value – exceeds your {rule}% threshold significantly',
    why_meets_criteria: 'This deal meets your investment criteria – safe to proceed',
    why_close_criteria: 'Close to your criteria – negotiate or reduce costs to improve',
    why_too_risky: 'Too risky at current terms – significant changes needed',
    
    // ========== PROPERTY DETAILS ==========
    prop_adjust_title: 'Adjust Property Details',
    prop_adjust_subtitle: 'Some property records may be outdated. You can adjust property details to improve comparable accuracy.',
    prop_beds: 'Bedrooms',
    prop_baths: 'Bathrooms',
    prop_sqft: 'Square Feet',
    prop_year_built: 'Year Built',
    prop_lot_size: 'Lot Size (acres)',
    prop_property_type: 'Property Type',
    prop_original_data: 'Original API Data',
    prop_user_adjusted: 'User Adjusted',
    prop_reset_to_original: 'Reset to Original',
    prop_type_single_family: 'Single Family',
    prop_type_duplex: 'Duplex',
    prop_type_triplex: 'Triplex',
    prop_type_fourplex: 'Fourplex',
    prop_type_multi_family: 'Multifamily (5+)',
    prop_type_condo: 'Condo',
    prop_type_townhouse: 'Townhouse',
    
    // ========== DEAL ASSUMPTIONS ==========
    assumptions_title: 'Deal Assumptions',
    assumptions_rehab_condition: 'Rehab Condition',
    assumptions_rehab_auto: 'Auto',
    assumptions_rehab_light: 'Light',
    assumptions_rehab_medium: 'Medium',
    assumptions_rehab_heavy: 'Heavy',
    assumptions_rehab_custom: 'Custom',
    assumptions_input_type: 'Input Type',
    assumptions_total_budget: 'Total Budget',
    assumptions_cost_per_sqft: 'Cost per SqFt',
    assumptions_total_rehab_budget: 'Total Rehab Budget',
    assumptions_holding_period: 'Holding Period',
    assumptions_months: 'months',
    assumptions_loan_interest: 'Loan Interest',
    assumptions_loan_points: 'Loan Points',
    assumptions_closing_costs: 'Closing Costs',
    assumptions_agent_commission: 'Agent Commission',
    
    // ========== DEAL TOLERANCE ==========
    tolerance_title: 'Deal Tolerance',
    tolerance_mode: 'Tolerance Mode',
    tolerance_strict: 'Strict',
    tolerance_balanced: 'Balanced',
    tolerance_aggressive: 'Aggressive',
    tolerance_custom: 'Custom',
    tolerance_price_label: 'Price Tolerance',
    tolerance_profit_label: 'Profit Tolerance',
    tolerance_price_help: 'How far above your Target % Rule can still be acceptable',
    tolerance_profit_help: 'How far below your profit target can still be acceptable',
    tolerance_strict_desc: 'Exact targets only - no flexibility',
    tolerance_balanced_desc: 'Small tolerance for near-target deals',
    tolerance_aggressive_desc: 'Wider tolerance for opportunity hunting',
    tolerance_custom_desc: 'Set your own tolerance values',
    tolerance_near_target: 'Slightly above your target, but still within your selected tolerance.',
    tolerance_near_profit: 'Just below your profit goal, but acceptable within your selected tolerance.',
    tolerance_near_both: 'Near target and still strong enough to consider.',
    assumptions_target_rule: 'Target % Rule',
    assumptions_property_taxes: 'Property Taxes/mo',
    assumptions_insurance: 'Insurance/mo',
    assumptions_utilities: 'Utilities/mo',
    assumptions_apply_recalculate: 'Apply Changes & Recalculate',
    assumptions_reset_defaults: 'Reset to System Defaults',
    assumptions_save_my_default: 'Save as My Default',
    assumptions_load_my_default: 'Load My Defaults',
    assumptions_calculated_values: 'Calculated Values',
    assumptions_rehab_cost: 'Rehab Cost',
    assumptions_net_profit: 'Net Profit',
    assumptions_rating: 'Rating',
    
    // ========== COMPARABLE SALES ==========
    comps_title: 'Comparable Sales',
    comps_subtitle: '{count} recent sales used to estimate property value',
    comps_tooltip: 'Comparable properties directly affect your estimated value and deal results. Custom comparables will update your analysis.',
    comps_sold_price: 'Sold Price',
    comps_sqft: 'Sq Ft',
    comps_beds: 'Beds',
    comps_baths: 'Baths',
    comps_distance: 'Distance',
    comps_sold_date: 'Sold Date',
    comps_best: 'Best',
    comps_sold: 'Sold',
    comps_avg_price_sqft: 'Average Price/Sq Ft',
    comps_subject_property: 'Subject Property',
    
    // ========== SCORE BREAKDOWN ==========
    score_breakdown: 'Score Breakdown',
    
    // ========== DEAL ANALYSIS ==========
    deal_analysis_title: 'Deal Analysis',
    deal_opportunities: 'Opportunities',
    deal_risk_factors: 'Risk Factors',
    
    // ========== FLIP ANALYSIS ==========
    flip_analysis_title: 'Fix & Flip Analysis',
    flip_purchase_price: 'Purchase Price',
    flip_rehab_cost: 'Rehab Cost',
    flip_loan_costs: 'Loan Costs',
    flip_holding_costs: 'Holding Costs',
    flip_agent_commission: 'Agent Commission',
    flip_closing_costs: 'Closing Costs',
    flip_total_project_cost: 'Total Project Cost',
    flip_net_profit: 'Net Profit',
    
    // ========== RENTAL ANALYSIS ==========
    rental_analysis_title: 'Rental Analysis',
    rental_monthly_rent: 'Monthly Rent',
    rental_total_monthly_rent: 'Total Monthly Rent',
    rental_monthly_cashflow: 'Monthly Cashflow',
    rental_cap_rate: 'Cap Rate',
    rental_cash_on_cash: 'Cash-on-Cash',
    rental_units: 'Units',
    rental_per_unit: 'per unit',
    rental_cash_invested: 'Cash Invested Breakdown',
    rental_down_payment: 'Down Payment',
    rental_total_cash_in: 'Total Cash In',
    
    // ========== ACTIONS ==========
    action_save: 'Save Deal',
    action_compare: 'Compare',
    action_share: 'Share',
    action_actions: 'Actions',
    action_preview_pdf: 'Preview PDF',
    action_download_pdf: 'Download PDF',
    action_send_report: 'Send Report',
    action_edit_assumptions: 'Edit Assumptions',
    action_recalculate: 'Recalculate',
    action_upgrade: 'Upgrade to Pro',
    action_back: 'Back',
    
    // ========== CTA ==========
    cta_find_next_deal: 'Find Your Next Profitable Deal',
    cta_analyze_before_risk: 'Analyze another property before you risk your next investment. Every deal deserves this level of scrutiny.',
    cta_analyze_another: 'Analyze Another Deal',
    
    // ========== DEAL INBOX ==========
    inbox_title: 'Deal Inbox',
    inbox_subtitle: 'Receive deals from wholesalers, agents, and partners',
    inbox_link_title: 'Your Submission Link',
    inbox_link_subtitle: 'Share this link with wholesalers, agents, or partners so they can submit investment opportunities directly to your inbox.',
    inbox_received: 'Received Submissions',
    inbox_no_submissions: 'No submissions yet',
    inbox_share_prompt: 'Share your inbox link to start receiving deals',
    inbox_analyze: 'Analyze Deal',
    inbox_status_pending: 'Pending',
    inbox_status_analyzed: 'Analyzed',
    inbox_status_accepted: 'Accepted',
    inbox_status_rejected: 'Rejected',
    inbox_all: 'All',
    inbox_submitted_by: 'Submitted By',
    inbox_internal_notes: 'Internal Notes',
    inbox_notes_placeholder: 'Add private notes about this deal...',
    inbox_mark_accepted: 'Accept Deal',
    inbox_mark_rejected: 'Reject Deal',
    inbox_url_or_address: 'Paste property link or address',
    inbox_url_help: 'Supports Zillow, Redfin, Realtor.com links',
    
    // ========== INVESTMENT CRITERIA ==========
    criteria_title: 'Investment Criteria (Your Inputs)',
    criteria_edit_my_criteria: 'Edit My Criteria',
    criteria_min_profit: 'Min Profit',
    criteria_min_margin: 'Min Margin',
    criteria_min_roi: 'Min ROI',
    criteria_min_cashflow: 'Min Cash Flow',
    criteria_min_cap_rate: 'Min Cap Rate',
    criteria_min_coc: 'Min CoC',
    
    // ========== ARV WARNING ==========
    arv_unable_compute: 'Value Cannot Be Computed',
    arv_low_confidence: 'Low Value Confidence Warning',
    arv_unable_msg: 'We could not compute a reliable estimated value for this property due to limited comparable data. For investment decisions, we recommend getting a professional appraisal.',
    arv_low_msg: 'The estimated value has low confidence due to limited comparable sales data. Consider this a rough estimate and verify with a professional appraisal for investment decisions.',
    arv_appraisal_recommended: 'Professional Appraisal Recommended',
    arv_fallback_step: 'Fallback Step',
    
    // ========== DEAL EXPLANATION ==========
    explain_deal_btn: 'Explain this deal',
    explain_value_btn: 'Explain Value',
    explain_deal_title: 'Why this score?',
    explain_score_high: 'Strong profit potential with {margin}% margin on a ${profit} net profit.',
    explain_score_medium: 'Moderate profit potential with {margin}% margin.',
    explain_score_low: 'Limited profit potential. Consider negotiating a lower price.',
    explain_arv_high: 'Value confidence is high based on {count} recent comparable sales nearby.',
    explain_arv_medium: 'Value estimate has medium confidence. More comparables would improve accuracy.',
    explain_arv_low: 'Value confidence is low. Limited comparable data available.',
    explain_rehab_light: 'Light rehab expected (${cost}/sqft) - cosmetic updates only.',
    explain_rehab_medium: 'Medium rehab required (${cost}/sqft) - kitchen/bath updates needed.',
    explain_rehab_heavy: 'Heavy rehab needed (${cost}/sqft) - significant renovation required.',
    explain_rental_strong: 'Strong rental metrics: {cap}% cap rate and ${cashflow}/month cashflow.',
    explain_rental_moderate: 'Rental returns are moderate with {cap}% cap rate.',
    explain_rental_weak: 'Rental metrics are below average. Better suited for flip strategy.',
    explain_risk_high: 'Multiple risk factors identified. Proceed with caution.',
    explain_opportunity: 'Opportunities identified: {count} positive factors.',
    explain_analysis_confidence: 'Analysis confidence',
    explain_data_quality_score: 'data quality score',
    
    // ========== DEAL CONFIDENCE ==========
    deal_confidence_title: 'Deal Score Confidence',
    deal_score_confidence: 'Deal Score Confidence',
    deal_confidence_tooltip: 'Deal Score Confidence shows how reliable the analysis is based on the available property and comparable data.',
    
    // ========== ADDRESS VERIFICATION ==========
    address_verification: 'Address Verification',
    address_you_entered: 'You entered',
    address_analyzing: 'Analyzing',
    
    // ========== STRATEGY BADGES ==========
    strategy_fix_flip: 'Fix & Flip',
    strategy_rental: 'Rental',
    strategy_brrrr: 'BRRRR',
    strategy_property_value: 'Property Value',
    
    // ========== PLANS ==========
    // Internal PRO = "Investor", Internal PRO_PLUS = "Pro"
    plan_free: 'Free',
    plan_pro: 'Investor',       // Display name for internal PRO
    plan_pro_plus: 'Pro',       // Display name for internal PRO_PLUS
    plan_free_desc: '3 lifetime analyses',
    plan_pro_desc: '50 analyses per month',
    plan_pro_plus_desc: '200 analyses per month',
    
    // ========== MESSAGES ==========
    msg_analyzing: 'Analyzing property...',
    msg_saved: 'Deal saved successfully',
    msg_error: 'An error occurred',
    msg_limit_reached: 'Monthly limit reached. Upgrade your plan for higher limits.',
    msg_recalc_failed: 'Recalculation failed',
    msg_pdf_downloaded: 'PDF report downloaded!',
    msg_pdf_failed: 'Failed to generate PDF',
    msg_email_required: 'Please enter at least one email address',
    msg_email_sent: 'Report sent successfully',
    msg_email_failed: 'Failed to send email',
    msg_property_updated: 'Property details updated',
    msg_reset_original: 'Reset to original data',
    msg_defaults_saved: 'Defaults saved!',
    msg_defaults_loaded: 'Defaults loaded!',
    msg_no_defaults: 'No saved defaults found',
    msg_criteria_saved: 'Investment criteria saved!',
    msg_criteria_failed: 'Failed to save criteria',
    msg_deal_updated: 'Deal analysis updated with new ARV',
    
    // ========== NO DATA ==========
    no_analysis_data: 'No analysis data found',
    analyze_property: 'Analyze a Property',
    
    // ========== EMAIL MODAL ==========
    email_title: 'Send Report via Email',
    email_subtitle: 'Enter email addresses to send the PDF report. Separate multiple emails with commas.',
    email_label: 'Email Address(es)',
    email_placeholder: 'partner@example.com, lender@bank.com',
    email_property: 'Property',
    email_deal_score: 'Deal Score',
    email_cancel: 'Cancel',
    email_send: 'Send Report',
    
    // ========== USAGE DISPLAY ==========
    usage_analyses: 'analyses',
    usage_remaining: 'remaining',
    usage_days_left: 'days left',
    usage_days_remaining: 'days remaining',
    usage_expired: 'Expired',
    usage_founder_access: 'Founder access - Full features',
    
    // ========== MULTI-FAMILY VALUATION ==========
    multifamily_title: 'Multi-Family Valuation (Blended Approach)',
    multifamily_sales_approach: 'Sales Approach',
    multifamily_income_approach: 'Income Approach',
    multifamily_final_value: 'Final Value',
    multifamily_blended: 'Blended',
    multifamily_weight: 'weight',
    multifamily_units: 'units',
    multifamily_unit_count: 'Unit Count',
    multifamily_rent_unit: 'Rent/Unit',
    multifamily_gross_annual_rent: 'Gross Annual Rent',
    multifamily_income_explanation: 'Income Approach: Annual Rent ÷ Cap Rate. For multi-family properties, blending sales comps with income valuation provides a more accurate estimate.',
    
    // ========== COMMON ==========
    common_original: 'Orig',
    common_current: 'Current',
    common_target: 'Target',
    common_rule: 'rule',
    common_based_on: 'based on',
    common_yes: 'Yes',
    common_no: 'No',
    common_cancel: 'Cancel',
    common_save: 'Save',
    common_apply: 'Apply',
    common_reset: 'Reset',
    common_close: 'Close',
    common_view: 'View',
    common_edit: 'Edit',
    common_delete: 'Delete',
  },
  
  es: {
    // ========== LANDING PAGE ==========
    hero_title: 'Copiloto IA para Inversionistas Inmobiliarios',
    hero_subtitle: 'Analiza Propiedades en Segundos',
    hero_description: 'Pega un enlace de Zillow, Redfin o Realtor o ingresa una dirección y obtén instantáneamente valor estimado, costo de remodelación, ganancia de reventa, análisis de renta y puntuación del trato.',
    cta_analyze: 'Analizar una Propiedad',
    cta_pricing: 'Ver Precios',
    cta_login: 'Iniciar Sesión',
    cta_get_started: 'Comenzar',
    
    // ========== NAVIGATION ==========
    nav_dashboard: 'Panel',
    nav_analyze: 'Analizar',
    nav_saved: 'Guardados',
    nav_compare: 'Comparar',
    nav_inbox: 'Buzón de Tratos',
    nav_account: 'Cuenta',
    nav_billing: 'Facturación',
    nav_logout: 'Cerrar Sesión',
    nav_criteria: 'Mis Criterios',
    nav_admin: 'Admin Analítica',
    
    // ========== ANALYZER INPUT ==========
    input_placeholder: 'Pega URL de propiedad o ingresa dirección',
    input_analyze_btn: 'Analizar',
    input_url_label: 'URL de Propiedad',
    input_address_label: 'Dirección',
    sample_deals_title: 'Ver un Análisis de Ejemplo',
    sample_deals_subtitle: 'Explora cómo CHEKSTE analiza propiedades de inversión reales. Haz clic en uno de los ejemplos para ver un análisis completo.',
    sample_fix_flip: 'Ejemplo de Fix & Flip',
    sample_rental: 'Ejemplo de Inversión en Renta',
    
    // ========== RESULTS PAGE - CORE NUMBERS ==========
    result_purchase_price: 'Precio de Compra',
    result_asking_price: 'Precio de Venta',
    result_current_value: 'Valor / Precio Actual',
    result_arv: 'Valor Estimado',
    result_arv_tooltip: 'Valor de reventa estimado basado en propiedades comparables en tu área',
    result_confidence: 'Confianza',
    result_rehab: 'Costo de Rehab',
    result_profit: 'Ganancia Neta',
    result_margin: 'Margen de Ganancia',
    result_mao: 'Precio Objetivo',
    result_mao_short: 'Precio Obj.',
    result_mao_tooltip: 'Precio máximo que debes pagar para cumplir tus criterios de inversión y mantener un margen rentable',
    result_equity_spread: 'Diferencial de Capital',
    result_equity_spread_tooltip: 'Diferencia entre el valor estimado y el precio de compra.',
    result_potential_equity: 'Capital Potencial',
    result_potential_equity_tooltip: 'Diferencia entre el valor estimado y el precio/valor actual',
    result_deal_score: 'Puntuación',
    result_cap_rate: 'Tasa de Cap.',
    result_cashflow: 'Flujo Mensual',
    result_your_input: 'Tu entrada',
    result_of_est_value: 'del Valor Est.',
    result_of_estimated_value: 'del valor estimado',
    
    // ========== DEAL CLASSIFICATION ==========
    deal_decision: 'Decisión del Trato',
    deal_buy: 'COMPRAR',
    deal_negotiate: 'NEGOCIAR',
    deal_pass: 'PASAR',
    deal_proceed: 'PROCEDER',
    deal_avoid: 'EVITAR',
    deal_lender_confidence: 'Confianza del Prestamista',
    deal_confidence_high: 'Alta',
    deal_confidence_moderate: 'Moderada',
    deal_confidence_low: 'Baja',
    deal_why_this_deal: 'Por Qué Este Trato',
    deal_how_to_improve: 'Cómo Mejorar Este Trato',
    deal_improvement_note: 'Recomendaciones conservadoras para proteger tu inversión:',
    deal_path_to_deal: 'Ruta al Trato',
    deal_path_note: '¿Qué hace que este trato entre en tu rango de compra?',
    deal_trust_signal: 'Basado en datos reales del mercado y ventas comparables',
    deal_financial_impact: 'Impacto Financiero',
    deal_below_target: 'Este trato está ~${gap} por debajo de tu ganancia objetivo',
    deal_above_target: 'Ganancia esperada: ${profit} (+${surplus} sobre objetivo)',
    deal_highest_impact: 'Mayor Impacto',
    deal_increases_profit: 'Aumenta ganancia en',
    deal_target_price: 'Precio objetivo',
    deal_target_rehab: 'Rehab objetivo',
    deal_lower_purchase: 'Reducir Precio de Compra',
    deal_reduce_rehab: 'Reducir Alcance de Rehab',
    deal_buy_cash: 'Comprar en Efectivo',
    deal_cash_mode_hint: 'Usa el toggle de Efectivo arriba',
    deal_negotiate_price: 'Negocia un precio de compra más bajo para mejorar tu margen de ganancia',
    deal_eliminate_financing: 'Eliminar costos de financiamiento mejora los márgenes significativamente',
    
    // ========== DEAL MODE TOGGLE ==========
    deal_mode: 'Modo del Trato',
    deal_mode_cash: 'Efectivo',
    deal_mode_financed: 'Financiado',
    
    // ========== PRICE TARGET COMPARISON ==========
    price_within_target: 'Precio Dentro de Tu Objetivo',
    price_exceeds_target: 'El Precio Excede Tu Objetivo',
    price_under_target: 'Bajo Objetivo',
    price_negotiation_gap: 'Brecha de negociación',
    price_target_rule: 'Regla Objetivo',
    price_note_profitable: 'Nota: Este trato muestra ganancia pero excede tu umbral de disciplina. Considera negociar.',
    price_based_on_rule: 'Basado en tu regla del {rule}%, el precio objetivo es ${target}. El precio (${price}) está {status} este umbral.',
    price_status_within: 'dentro de',
    price_status_above: 'por encima de',
    
    // ========== WHY BULLETS ==========
    why_profit_exceeds: 'Ganancia neta de ${profit} excede tu objetivo de ${target}',
    why_profit_short: 'Ganancia neta de ${profit} está ~${gap} por debajo del objetivo',
    why_profit_negative: 'Ganancia negativa de ${profit} – requiere negociación significativa',
    why_investment_healthy: 'Inversión total es {pct}% del valor estimado (margen saludable)',
    why_investment_tight: 'Inversión total es {pct}% del valor – márgenes ajustados',
    why_investment_high: 'Inversión total es {pct}% del valor – demasiado alto para flip seguro',
    // NEW: Keys that reference user's active rule for consistency
    why_investment_healthy_rule: 'Inversión total es {pct}% del valor estimado (dentro de tu umbral de {rule}%)',
    why_investment_tight_rule: 'Inversión total es {pct}% del valor – por encima de tu umbral de {rule}%',
    why_investment_high_rule: 'Inversión total es {pct}% del valor – excede significativamente tu umbral de {rule}%',
    why_meets_criteria: 'Este trato cumple tus criterios de inversión – seguro para proceder',
    why_close_criteria: 'Cerca de tus criterios – negocia o reduce costos para mejorar',
    why_too_risky: 'Muy riesgoso en términos actuales – cambios significativos necesarios',
    
    // ========== PROPERTY DETAILS ==========
    prop_adjust_title: 'Ajustar Detalles de Propiedad',
    prop_adjust_subtitle: 'Algunos registros pueden estar desactualizados. Puedes ajustar los detalles para mejorar la precisión.',
    prop_beds: 'Habitaciones',
    prop_baths: 'Baños',
    prop_sqft: 'Pies Cuadrados',
    prop_year_built: 'Año de Construcción',
    prop_lot_size: 'Tamaño del Lote (acres)',
    prop_property_type: 'Tipo de Propiedad',
    prop_original_data: 'Datos Originales',
    prop_user_adjusted: 'Ajustado por Usuario',
    prop_reset_to_original: 'Restablecer Original',
    prop_type_single_family: 'Unifamiliar',
    prop_type_duplex: 'Dúplex',
    prop_type_triplex: 'Tríplex',
    prop_type_fourplex: 'Cuádruplex',
    prop_type_multi_family: 'Multifamiliar (5+)',
    prop_type_condo: 'Condominio',
    prop_type_townhouse: 'Casa Adosada',
    
    // ========== DEAL ASSUMPTIONS ==========
    assumptions_title: 'Supuestos del Trato',
    assumptions_rehab_condition: 'Condición de Rehab',
    assumptions_rehab_auto: 'Auto',
    assumptions_rehab_light: 'Leve',
    assumptions_rehab_medium: 'Medio',
    assumptions_rehab_heavy: 'Pesado',
    assumptions_rehab_custom: 'Personalizado',
    assumptions_input_type: 'Tipo de Entrada',
    assumptions_total_budget: 'Presupuesto Total',
    assumptions_cost_per_sqft: 'Costo por Pie²',
    assumptions_total_rehab_budget: 'Presupuesto Total de Rehab',
    assumptions_holding_period: 'Período de Tenencia',
    assumptions_months: 'meses',
    assumptions_loan_interest: 'Interés del Préstamo',
    assumptions_loan_points: 'Puntos del Préstamo',
    assumptions_closing_costs: 'Costos de Cierre',
    assumptions_agent_commission: 'Comisión del Agente',
    
    // ========== DEAL TOLERANCE ==========
    tolerance_title: 'Tolerancia del Trato',
    tolerance_mode: 'Modo de Tolerancia',
    tolerance_strict: 'Estricto',
    tolerance_balanced: 'Equilibrado',
    tolerance_aggressive: 'Agresivo',
    tolerance_custom: 'Personalizado',
    tolerance_price_label: 'Tolerancia de Precio',
    tolerance_profit_label: 'Tolerancia de Ganancia',
    tolerance_price_help: 'Cuánto por encima de tu Regla % Objetivo aún puede ser aceptable',
    tolerance_profit_help: 'Cuánto por debajo de tu objetivo de ganancia aún puede ser aceptable',
    tolerance_strict_desc: 'Solo objetivos exactos - sin flexibilidad',
    tolerance_balanced_desc: 'Pequeña tolerancia para tratos cercanos',
    tolerance_aggressive_desc: 'Mayor tolerancia para buscar oportunidades',
    tolerance_custom_desc: 'Establece tus propios valores de tolerancia',
    tolerance_near_target: 'Ligeramente por encima de tu objetivo, pero dentro de tu tolerancia seleccionada.',
    tolerance_near_profit: 'Justo por debajo de tu meta de ganancia, pero aceptable dentro de tu tolerancia.',
    tolerance_near_both: 'Cerca del objetivo y aún lo suficientemente fuerte para considerar.',
    
    assumptions_target_rule: 'Regla % Objetivo',
    assumptions_property_taxes: 'Impuestos/mes',
    assumptions_insurance: 'Seguro/mes',
    assumptions_utilities: 'Servicios/mes',
    assumptions_apply_recalculate: 'Aplicar y Recalcular',
    assumptions_reset_defaults: 'Restaurar Predeterminados',
    assumptions_save_my_default: 'Guardar como Mi Predeterminado',
    assumptions_load_my_default: 'Cargar Mis Predeterminados',
    assumptions_calculated_values: 'Valores Calculados',
    assumptions_rehab_cost: 'Costo Rehab',
    assumptions_net_profit: 'Ganancia Neta',
    assumptions_rating: 'Calificación',
    
    // ========== COMPARABLE SALES ==========
    comps_title: 'Ventas Comparables',
    comps_subtitle: '{count} ventas recientes usadas para estimar el valor',
    comps_tooltip: 'Las propiedades comparables afectan directamente tu valor estimado y resultados. Los comparables personalizados actualizarán tu análisis.',
    comps_sold_price: 'Precio Venta',
    comps_sqft: 'Pies²',
    comps_beds: 'Hab.',
    comps_baths: 'Baños',
    comps_distance: 'Distancia',
    comps_sold_date: 'Fecha Venta',
    comps_best: 'Mejor',
    comps_sold: 'Vendido',
    comps_avg_price_sqft: 'Precio Promedio/Pie²',
    comps_subject_property: 'Propiedad Sujeta',
    
    // ========== SCORE BREAKDOWN ==========
    score_breakdown: 'Desglose de Puntuación',
    
    // ========== DEAL ANALYSIS ==========
    deal_analysis_title: 'Análisis del Trato',
    deal_opportunities: 'Oportunidades',
    deal_risk_factors: 'Factores de Riesgo',
    
    // ========== FLIP ANALYSIS ==========
    flip_analysis_title: 'Análisis de Reventa',
    flip_purchase_price: 'Precio de Compra',
    flip_rehab_cost: 'Costo de Rehab',
    flip_loan_costs: 'Costos de Préstamo',
    flip_holding_costs: 'Costos de Mantenimiento',
    flip_agent_commission: 'Comisión Agente',
    flip_closing_costs: 'Costos de Cierre',
    flip_total_project_cost: 'Costo Total del Proyecto',
    flip_net_profit: 'Ganancia Neta',
    
    // ========== RENTAL ANALYSIS ==========
    rental_analysis_title: 'Análisis de Renta',
    rental_monthly_rent: 'Renta Mensual',
    rental_total_monthly_rent: 'Renta Mensual Total',
    rental_monthly_cashflow: 'Flujo Mensual',
    rental_cap_rate: 'Tasa de Cap.',
    rental_cash_on_cash: 'Rendimiento Efectivo',
    rental_units: 'Unidades',
    rental_per_unit: 'por unidad',
    rental_cash_invested: 'Desglose de Efectivo Invertido',
    rental_down_payment: 'Enganche',
    rental_total_cash_in: 'Total Efectivo',
    
    // ========== ACTIONS ==========
    action_save: 'Guardar',
    action_compare: 'Comparar',
    action_share: 'Compartir',
    action_actions: 'Acciones',
    action_preview_pdf: 'Vista Previa PDF',
    action_download_pdf: 'Descargar PDF',
    action_send_report: 'Enviar Reporte',
    action_edit_assumptions: 'Editar Supuestos',
    action_recalculate: 'Recalcular',
    action_upgrade: 'Mejorar a Pro',
    action_back: 'Volver',
    
    // ========== CTA ==========
    cta_find_next_deal: 'Encuentra Tu Próximo Trato Rentable',
    cta_analyze_before_risk: 'Analiza otra propiedad antes de arriesgar tu próxima inversión. Cada trato merece este nivel de escrutinio.',
    cta_analyze_another: 'Analizar Otro Trato',
    
    // ========== DEAL INBOX ==========
    inbox_title: 'Buzón de Tratos',
    inbox_subtitle: 'Recibe tratos de mayoristas, agentes y socios',
    inbox_link_title: 'Tu Enlace de Envío',
    inbox_link_subtitle: 'Comparte este enlace con mayoristas, agentes o socios para que puedan enviarte oportunidades de inversión directamente.',
    inbox_received: 'Envíos Recibidos',
    inbox_no_submissions: 'Sin envíos aún',
    inbox_share_prompt: 'Comparte tu enlace para empezar a recibir tratos',
    inbox_analyze: 'Analizar Trato',
    inbox_status_pending: 'Pendiente',
    inbox_status_analyzed: 'Analizado',
    inbox_status_accepted: 'Aceptado',
    inbox_status_rejected: 'Rechazado',
    inbox_all: 'Todos',
    inbox_submitted_by: 'Enviado Por',
    inbox_internal_notes: 'Notas Internas',
    inbox_notes_placeholder: 'Agrega notas privadas sobre este trato...',
    inbox_mark_accepted: 'Aceptar Trato',
    inbox_mark_rejected: 'Rechazar Trato',
    inbox_url_or_address: 'Pega enlace de propiedad o dirección',
    inbox_url_help: 'Soporta enlaces de Zillow, Redfin, Realtor.com',
    
    // ========== INVESTMENT CRITERIA ==========
    criteria_title: 'Criterios de Inversión (Tus Entradas)',
    criteria_edit_my_criteria: 'Editar Mis Criterios',
    criteria_min_profit: 'Gan. Mín',
    criteria_min_margin: 'Margen Mín',
    criteria_min_roi: 'ROI Mín',
    criteria_min_cashflow: 'Flujo Mín',
    criteria_min_cap_rate: 'Cap Rate Mín',
    criteria_min_coc: 'CoC Mín',
    
    // ========== ARV WARNING ==========
    arv_unable_compute: 'No se Puede Calcular el Valor',
    arv_low_confidence: 'Advertencia de Confianza Baja',
    arv_unable_msg: 'No pudimos calcular un valor estimado confiable para esta propiedad debido a datos comparables limitados. Para decisiones de inversión, recomendamos obtener una tasación profesional.',
    arv_low_msg: 'El valor estimado tiene baja confianza debido a datos de ventas comparables limitados. Considera esto como una estimación aproximada y verifica con una tasación profesional.',
    arv_appraisal_recommended: 'Se Recomienda Tasación Profesional',
    arv_fallback_step: 'Paso de Respaldo',
    
    // ========== DEAL EXPLANATION ==========
    explain_deal_btn: 'Explicar este trato',
    explain_value_btn: 'Explicar Valor',
    explain_deal_title: '¿Por qué esta puntuación?',
    explain_score_high: 'Fuerte potencial de ganancia con {margin}% de margen en una ganancia neta de ${profit}.',
    explain_score_medium: 'Potencial de ganancia moderado con {margin}% de margen.',
    explain_score_low: 'Potencial de ganancia limitado. Considera negociar un precio más bajo.',
    explain_arv_high: 'Confianza del valor alta basada en {count} ventas comparables recientes cercanas.',
    explain_arv_medium: 'Estimación del valor con confianza media. Más comparables mejorarían la precisión.',
    explain_arv_low: 'Confianza del valor baja. Datos comparables limitados disponibles.',
    explain_rehab_light: 'Rehab leve esperado (${cost}/pie²) - solo actualizaciones cosméticas.',
    explain_rehab_medium: 'Rehab medio requerido (${cost}/pie²) - actualizaciones de cocina/baño necesarias.',
    explain_rehab_heavy: 'Rehab pesado necesario (${cost}/pie²) - renovación significativa requerida.',
    explain_rental_strong: 'Métricas de renta sólidas: {cap}% tasa de cap. y ${cashflow}/mes flujo.',
    explain_rental_moderate: 'Rendimientos de renta moderados con {cap}% tasa de cap.',
    explain_rental_weak: 'Métricas de renta por debajo del promedio. Más adecuado para reventa.',
    explain_risk_high: 'Múltiples factores de riesgo identificados. Procede con precaución.',
    explain_opportunity: 'Oportunidades identificadas: {count} factores positivos.',
    explain_analysis_confidence: 'Confianza del análisis',
    explain_data_quality_score: 'puntuación de calidad de datos',
    
    // ========== DEAL CONFIDENCE ==========
    deal_confidence_title: 'Confianza de la Puntuación',
    deal_score_confidence: 'Confianza de Puntuación',
    deal_confidence_tooltip: 'La Confianza de la Puntuación muestra qué tan confiable es el análisis basado en los datos disponibles.',
    
    // ========== ADDRESS VERIFICATION ==========
    address_verification: 'Verificación de Dirección',
    address_you_entered: 'Ingresaste',
    address_analyzing: 'Analizando',
    
    // ========== STRATEGY BADGES ==========
    strategy_fix_flip: 'Fix & Flip',
    strategy_rental: 'Renta',
    strategy_brrrr: 'BRRRR',
    strategy_property_value: 'Valor de Propiedad',
    
    // ========== PLANS ==========
    // Internal PRO = "Investor", Internal PRO_PLUS = "Pro"
    plan_free: 'Gratis',
    plan_pro: 'Investor',       // Display name for internal PRO
    plan_pro_plus: 'Pro',       // Display name for internal PRO_PLUS
    plan_free_desc: '3 análisis de por vida',
    plan_pro_desc: '50 análisis por mes',
    plan_pro_plus_desc: '200 análisis por mes',
    
    // ========== MESSAGES ==========
    msg_analyzing: 'Analizando propiedad...',
    msg_saved: 'Trato guardado exitosamente',
    msg_error: 'Ocurrió un error',
    msg_limit_reached: 'Límite mensual alcanzado. Mejora tu plan para mayores límites.',
    msg_recalc_failed: 'Error al recalcular',
    msg_pdf_downloaded: '¡Reporte PDF descargado!',
    msg_pdf_failed: 'Error al generar PDF',
    msg_email_required: 'Ingresa al menos una dirección de correo',
    msg_email_sent: 'Reporte enviado exitosamente',
    msg_email_failed: 'Error al enviar correo',
    msg_property_updated: 'Detalles de propiedad actualizados',
    msg_reset_original: 'Restablecido a datos originales',
    msg_defaults_saved: '¡Predeterminados guardados!',
    msg_defaults_loaded: '¡Predeterminados cargados!',
    msg_no_defaults: 'No se encontraron predeterminados guardados',
    msg_criteria_saved: '¡Criterios de inversión guardados!',
    msg_criteria_failed: 'Error al guardar criterios',
    msg_deal_updated: 'Análisis actualizado con nuevo valor estimado',
    
    // ========== NO DATA ==========
    no_analysis_data: 'No se encontraron datos de análisis',
    analyze_property: 'Analizar una Propiedad',
    
    // ========== EMAIL MODAL ==========
    email_title: 'Enviar Reporte por Correo',
    email_subtitle: 'Ingresa direcciones de correo para enviar el reporte PDF. Separa múltiples correos con comas.',
    email_label: 'Dirección(es) de Correo',
    email_placeholder: 'socio@ejemplo.com, prestamista@banco.com',
    email_property: 'Propiedad',
    email_deal_score: 'Puntuación',
    email_cancel: 'Cancelar',
    email_send: 'Enviar Reporte',
    
    // ========== USAGE DISPLAY ==========
    usage_analyses: 'análisis',
    usage_remaining: 'restantes',
    usage_days_left: 'días restantes',
    usage_days_remaining: 'días restantes',
    usage_expired: 'Expirado',
    usage_founder_access: 'Acceso fundador - Todas las funciones',
    
    // ========== MULTI-FAMILY VALUATION ==========
    multifamily_title: 'Valoración Multi-Familiar (Enfoque Combinado)',
    multifamily_sales_approach: 'Enfoque de Ventas',
    multifamily_income_approach: 'Enfoque de Ingresos',
    multifamily_final_value: 'Valor Final',
    multifamily_blended: 'Combinado',
    multifamily_weight: 'peso',
    multifamily_units: 'unidades',
    multifamily_unit_count: 'Cantidad Unidades',
    multifamily_rent_unit: 'Renta/Unidad',
    multifamily_gross_annual_rent: 'Renta Anual Bruta',
    multifamily_income_explanation: 'Enfoque de Ingresos: Renta Anual ÷ Tasa Cap. Para propiedades multi-familiares, combinar ventas con valoración por ingresos proporciona una estimación más precisa.',
    
    // ========== COMMON ==========
    common_original: 'Orig',
    common_current: 'Actual',
    common_target: 'Meta',
    common_rule: 'regla',
    common_based_on: 'basado en',
    common_yes: 'Sí',
    common_no: 'No',
    common_cancel: 'Cancelar',
    common_save: 'Guardar',
    common_apply: 'Aplicar',
    common_reset: 'Restablecer',
    common_close: 'Cerrar',
    common_view: 'Ver',
    common_edit: 'Editar',
    common_delete: 'Eliminar',
  }
};

// ---------------------------------------------------------------------------
// LANGUAGE IS REACTIVE STATE, NOT A PAGE RELOAD.
//
// setLanguage used to call window.location.reload(). Reading the language from
// localStorage at render time gives no reactivity, so the reload was how the new
// language reached the screen — and it destroyed every piece of unsaved React state on
// the way. A user who typed their own ARV and then switched language watched their
// figure revert to our estimate and the "your figure" marker vanish: their number was
// silently replaced by ours. Staged rental edits died the same way.
//
// The language now lives in a context, so switching it re-renders and changes nothing
// else. localStorage is still written, so the choice survives an actual reload.
// ---------------------------------------------------------------------------
export const LANG_STORAGE_KEY = 'chekdeal_lang';

export const readStoredLang = () => {
  try {
    return localStorage.getItem(LANG_STORAGE_KEY) || 'en';
  } catch (e) {
    return 'en';   // private mode / storage disabled
  }
};

export const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(readStoredLang);

  const setLanguage = useCallback((newLang) => {
    const next = newLang === 'es' ? 'es' : 'en';
    try {
      localStorage.setItem(LANG_STORAGE_KEY, next);
    } catch (e) {
      /* storage disabled — the switch still applies for this session */
    }
    setLang(next);
  }, []);

  const value = useMemo(() => ({ lang, setLanguage }), [lang, setLanguage]);
  return React.createElement(LanguageContext.Provider, { value }, children);
};

export const useTranslation = () => {
  // Falls back to the stored value when rendered outside the provider, so a component
  // mounted in isolation (tests, storybook) still translates instead of throwing.
  const ctx = useContext(LanguageContext);
  const lang = ctx ? ctx.lang : readStoredLang();

  const t = (key) => {
    return translations[lang]?.[key] || translations.en?.[key] || key;
  };

  const setLanguage = ctx ? ctx.setLanguage : ((newLang) => {
    try { localStorage.setItem(LANG_STORAGE_KEY, newLang); } catch (e) {}
    window.location.reload();
  });

  return { t, lang, setLanguage };
};
